import { Stack, Typography, Box, ButtonBase } from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconArrowBack, IconChevronRight, IconStudyNote, IconDocument, IconPracticeQuiz } from "@/assets/icons";
import { AppRoutes } from "@/routes/config";
import { text, primary, divider, background, icon, red, state } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { spacing, rounded } from "@/theme/variables";
import { TRecommendation, TStudySubject, TSubjectLevelDTO, useSubjectLevels, useRecommendationQuery } from "@/infrastructure/adapters/study";
import { useStudyAccessGuard } from "@/providers/StudyAccessGuardProvider";
import { SubjectContentLevelItem, StudyOutlineButton } from "@/features/study/components/SubjectContentLevelItem";
import { getLevelElementId } from "@/features/study/utils/getLevelElementId";
import { navigateToRecommendation, THighlightAction, TRecommendationScrollState } from "@/features/study/utils/navigateToRecommendation";
import { LEVEL_ACTION_HIGHLIGHT_DURATION_MS } from "@/features/study/constants/level";
import { LoadingArea } from "@/components/elements/Loading/Loading";

/** Word badge colors (red theme from spec) */
const WORD_BADGE = { bg: state.error_3, text: red[500] } as const;
const LEVEL_SCROLL_MARGIN_TOP = "120px";

type Props = {
  subject: TStudySubject;
  levels: TSubjectLevelDTO[];
  recommendation: TRecommendation | null;
  vocabularyCount: number;
  learnedVocabularyCount: number;
  textbookFileUrl: string;
  isVocabularyQuizCompleted: boolean;
};

/** Scroll to a level element and set highlight state */
const scrollToLevelAndHighlight = (
  level: string,
  action: THighlightAction,
  setHighlight: (val: { level: string; action: THighlightAction } | null) => void,
) => {
  const elementId = getLevelElementId(level);
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
    setHighlight({ level, action });
  }
};

/** Subject content page for hasLevelAssessment subjects (STUDY_L_MainContents-02) */
export const SubjectContentContainer: FC<Props> = ({
  subject,
  levels,
  recommendation,
  textbookFileUrl,
  vocabularyCount,
  learnedVocabularyCount,
  isVocabularyQuizCompleted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [highlight, setHighlight] = useState<{ level: string; action: THighlightAction } | null>(null);

  /** Handle scroll state from SuggestedTaskCard navigation */
  useEffect(() => {
    const scrollState = location.state as TRecommendationScrollState | null;
    if (!scrollState?.scrollToLevel || !scrollState?.highlightAction) return;

    const timer = setTimeout(() => {
      scrollToLevelAndHighlight(scrollState.scrollToLevel, scrollState.highlightAction, setHighlight);
      window.history.replaceState({}, document.title);
    }, 300);
    return () => clearTimeout(timer);
  }, [location.state]);

  /** Auto-clear highlight after duration */
  useEffect(() => {
    if (!highlight) return;
    const timer = setTimeout(() => setHighlight(null), LEVEL_ACTION_HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlight]);

  const handleBack = useCallback(() => navigate("/study"), [navigate]);
  const handleTextExplanation = useCallback(() => {
    navigate(AppRoutes.studyTextExplanation.replace(":subjectId", subject.id), {
      state: { pdfUrl: textbookFileUrl },
    });
  }, [navigate, subject.id, textbookFileUrl]);

  /** Scroll to the recommended level block and highlight the action button */
  const handleNextTaskClick = useCallback(() => {
    if (!recommendation) return;
    navigateToRecommendation(navigate, recommendation);
  }, [navigate, recommendation]);

  return (
    <Stack>
      {/* Back button — sticky at top while scrolling */}
      <Box
        onClick={handleBack}
        sx={{
          position: "sticky",
          top: -16,
          zIndex: 1,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          bgcolor: background.primary,
          cursor: "pointer",
        }}
      >
        <IconArrowBack style={{ fontSize: 16, color: icon.default }} />
        <Typography sx={{ ...typography.body14Bold, color: text.secondary }}>{t("common.back")}</Typography>
      </Box>

      {/* Content below back button */}
      <Stack sx={{ gap: 2.5 }}>
        {/* Subject title */}
        <Typography sx={{ ...typography.sub16Bold, color: text.primary }}>{subject.name}</Typography>

        {/* Weekly label */}
        <Typography sx={{ ...typography.body14Bold, color: primary[500] }}>{t("study.subjectContent.weeklySubject")}</Typography>

        {/* Next task card */}
        {recommendation && (
          <ButtonBase
            onClick={handleNextTaskClick}
            sx={{
              py: "10px",
              px: spacing.sm,
              gap: 0.5,
              borderRadius: rounded.xs,
              border: `1px solid ${primary[500]}`,
              bgcolor: background.secondary,
              cursor: "pointer",
              textAlign: "left",
            }}
          >
            <Stack sx={{ flex: 1, gap: 0.5 }}>
              <Typography sx={{ ...typography.body14Bold, color: text.primary }}>{t("study.subjectContent.nextTask")}</Typography>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <IconStudyNote style={{ fontSize: 20, color: primary[500] }} />
                <Typography sx={{ ...typography.body14Semi, color: text.primary, flex: 1 }}>{recommendation.displayText}</Typography>
                <IconChevronRight style={{ fontSize: 20 }} color={icon.default} />
              </Stack>
            </Stack>
          </ButtonBase>
        )}

        {/* Text explanation row */}
        {textbookFileUrl && (
          <Box
            onClick={handleTextExplanation}
            sx={{
              display: "flex",
              alignItems: "center",
              py: "10px",
              px: spacing.sm,
              borderRadius: rounded.xs,
              border: `1px solid ${divider.high}`,
              bgcolor: background.white,
              cursor: "pointer",
            }}
          >
            <Typography sx={{ ...typography.body14Semi, color: text.primary, flex: 1 }}>{t("study.subjectContent.textExplanation")}</Typography>
            <IconDocument style={{ fontSize: 20, color: text.secondary }} />
          </Box>
        )}

        {/* Word study section */}
        <WordStudySection
          wordCount={vocabularyCount}
          learnedCount={learnedVocabularyCount}
          subjectId={subject.id}
          isVocabularyQuizCompleted={isVocabularyQuizCompleted}
        />

        {/* 練習問題 section */}
        <PracticeSection subjectId={subject.id} levels={levels} highlight={highlight} />
      </Stack>
    </Stack>
  );
};

/** 単語学習 section with word badge + practice/test buttons */
const WordStudySection: FC<{ wordCount: number; learnedCount: number; subjectId: string; isVocabularyQuizCompleted: boolean }> = ({
  wordCount,
  learnedCount,
  subjectId,
  isVocabularyQuizCompleted,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handlePractice = useCallback(() => {
    navigate(AppRoutes.studyWordPractice.replace(":subjectId", subjectId));
  }, [navigate, subjectId]);

  const handleQuiz = useCallback(() => {
    navigate(AppRoutes.studyWordQuiz.replace(":subjectId", subjectId));
  }, [navigate, subjectId]);

  return (
    <Stack sx={{ border: `1px solid ${divider.high}`, borderRadius: rounded.xs, overflow: "hidden", bgcolor: background.white }}>
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: spacing.sm, pt: spacing.sm, pb: 1 }}>
        <IconStudyNote style={{ fontSize: 20, color: primary[500] }} />
        <Typography sx={{ ...typography.body14Bold, color: text.primary }}>{t("study.subjectContent.wordStudy")}</Typography>
      </Stack>
      <Stack sx={{ mx: spacing.sm, mb: spacing.sm, border: `1px solid ${divider.high}`, borderRadius: rounded.xs, overflow: "hidden" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: spacing.sm,
            py: spacing.xs,
            bgcolor: primary[800],
          }}
        >
          <Box sx={{ px: spacing.sm, py: spacing.xs, borderRadius: rounded.xs, bgcolor: WORD_BADGE.bg }}>
            <Typography sx={{ ...typography.body14Bold, color: WORD_BADGE.text }}>{t("study.subjectContent.wordBadge")}</Typography>
          </Box>
          <Typography sx={{ ...typography.body14Semi, color: text.primary, flex: 1 }}>
            {t("study.subjectContent.wordCount", { count: wordCount })}
          </Typography>
          <Typography sx={{ ...typography.body14Semi, color: text.primary }}>
            {t("study.subjectContent.achievement", { learned: learnedCount, total: wordCount })}
          </Typography>
        </Box>
        <Stack sx={{ gap: 1, p: spacing.sm }}>
          <StudyOutlineButton label={t("study.subjectContent.practice")} onClick={handlePractice} />
          <StudyOutlineButton label={t("study.subjectContent.confirmTest")} completed={isVocabularyQuizCompleted} onClick={handleQuiz} />
        </Stack>
      </Stack>
    </Stack>
  );
};

/** Extract numeric level from "lv1" → 1 */
const parseLevelNum = (level: string): number => {
  const num = parseInt(level.replace("lv", ""), 10);
  return isNaN(num) ? 1 : num;
};

type TPracticeSectionProps = {
  subjectId: string;
  levels: TSubjectLevelDTO[];
  highlight: { level: string; action: THighlightAction } | null;
};

/** 練習問題 section — receives levels from parent */
const PracticeSection: FC<TPracticeSectionProps> = ({ subjectId, levels, highlight }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handlePractice = useCallback(
    (levelNum: number) => {
      navigate(AppRoutes.studyLevelPractice.replace(":subjectId", subjectId).replace(":level", String(levelNum)));
    },
    [navigate, subjectId],
  );

  const handleQuiz = useCallback(
    (levelNum: number) => {
      navigate(AppRoutes.studyLevelQuiz.replace(":subjectId", subjectId).replace(":level", String(levelNum)));
    },
    [navigate, subjectId],
  );

  if (levels.length === 0) return null;

  return (
    <Stack sx={{ border: `1px solid ${divider.high}`, borderRadius: rounded.xs, overflow: "hidden", bgcolor: background.white }}>
      {/* Section header */}
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ px: spacing.sm, pt: spacing.sm, pb: 1 }}>
        <IconPracticeQuiz style={{ fontSize: 20, color: primary[500] }} />
        <Typography sx={{ ...typography.body14Bold, color: text.primary }}>{t("study.subjectContent.practiceQuestions")}</Typography>
      </Stack>

      {/* Level items */}
      <Stack sx={{ mx: spacing.sm, mb: spacing.sm, gap: 1.5 }}>
        {levels.map((dto) => {
          const levelNum = parseLevelNum(dto.level);
          const mapped = {
            level: levelNum,
            practiceCorrectCount: dto.practiceCorrectCount,
            questionCount: dto.questionCount,
            testCompleted: dto.isQuizCompleted,
          };
          const isHighlighted = highlight?.level === dto.level;
          return (
            <Stack
              id={getLevelElementId(dto.level)}
              key={dto.level}
              sx={{
                scrollMarginTop: LEVEL_SCROLL_MARGIN_TOP,
                border: `1px solid ${divider.high}`,
                borderRadius: rounded.xs,
                overflow: "hidden",
              }}
            >
              <SubjectContentLevelItem
                level={mapped}
                onPractice={() => handlePractice(levelNum)}
                onTest={() => handleQuiz(levelNum)}
                highlightAction={isHighlighted ? highlight.action : undefined}
              />
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
};

/** Route wrapper — fetches subject + content + levels data, then renders SubjectContentPage.
 *  Subject comes from StudyAccessGuardProvider context (no extra API call). */
export const SubjectContentPage: FC = () => {
  const { subjectId } = useParams<{ subjectId: string }>();
  const { subject } = useStudyAccessGuard();
  const { data: recommendation, isLoading: isLoadingRecommendation } = useRecommendationQuery();
  const {
    levels,
    textbookFileUrl,
    vocabularyCount,
    learnedVocabularyCount,
    isVocabularyQuizCompleted,
    isLoading: isLevelsLoading,
  } = useSubjectLevels(subjectId ?? "");

  /** Only show recommendation if it belongs to the current subject */
  const subjectRecommendation = recommendation && String(recommendation.publishedSubjectId) === subjectId ? recommendation : null;

  if (isLoadingRecommendation || isLevelsLoading) {
    return <LoadingArea />;
  }

  return (
    <SubjectContentContainer
      subject={subject}
      levels={levels}
      recommendation={subjectRecommendation}
      vocabularyCount={vocabularyCount}
      learnedVocabularyCount={learnedVocabularyCount}
      textbookFileUrl={textbookFileUrl}
      isVocabularyQuizCompleted={isVocabularyQuizCompleted}
    />
  );
};

export default SubjectContentPage;
